import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import type { LabelListDownload } from './useLabelListDownload';

type LabelListDownloadButtonProps = {
    download: LabelListDownload;
    testId: string;
    size?: 'sm' | 'md';
    /** Layout only: how the button sits in its row. */
    className?: string;
    /** The file icon before the label, as on the import's labels card. */
    withIcon?: boolean;
};

/**
 * **Download label list (.xlsx)** for the import's labels card and the
 * Barcode issues success alert: it puts the workbook's sheet, headers and
 * file name into words (rulebook §7) and hands them to the download, which
 * builds and saves the file.
 */
export const LabelListDownloadButton: React.FC<
    LabelListDownloadButtonProps
> = ({ download, testId, size, className, withIcon = false }) => {
    const { t } = useTranslation('components');

    const onPress = () =>
        download.onDownload({
            labels: {
                sheet: t('labelList.sheet'),
                columns: {
                    product: t('labelList.columns.product'),
                    variant: t('labelList.columns.variant'),
                    sku: t('labelList.columns.sku'),
                    barcode: t('labelList.columns.barcode'),
                    labels: t('labelList.columns.labels'),
                },
            },
            fileName: t('labelList.fileName', { date: download.fileDate }),
        });

    return (
        <Button
            className={className}
            isPending={download.isDownloading}
            onPress={onPress}
            size={size}
            variant="secondary"
            {...getNodeTestIdProp(testId)}
        >
            {withIcon ? (
                <Icon aria-hidden icon="gravity-ui:file-arrow-down" />
            ) : null}
            {t('labelList.download')}
        </Button>
    );
};
